export default class ServiceMap {
    private static systemSettingsApi: any = null;
    private static serviceMap: any = null;
    private static cacheTimestamp: number = 0;
    private static readonly CACHE_DURATION = 600000; // 10 minutes
    private static ongoingRequest: Promise<any> | null = null;
    
    /**
     * Lazy load SystemSetting to avoid circular dependency
     */
    private static async getSystemSettingsApi(): Promise<any> {
        if (!this.systemSettingsApi) {
            const { default: SystemSetting } = await import("@/API/SystemSetting/SystemSetting");
            this.systemSettingsApi = new SystemSetting();
        }
        return this.systemSettingsApi;
    }
    
    /**
     * Get service map with intelligent caching and request deduplication
     * Multiple simultaneous calls will share the same request to prevent API spam
     */
    static async getServiceMap(): Promise<any> {
        const now = Date.now();
        
        // Return cached data if it's still valid
        if (this.serviceMap && (now - this.cacheTimestamp) < this.CACHE_DURATION) {
            return this.serviceMap;
        }
        
        // If there's already an ongoing request, wait for it instead of making a new one
        if (this.ongoingRequest) {
            return await this.ongoingRequest;
        }
        
        // Start new request and cache the promise to prevent duplicate requests
        this.ongoingRequest = this.fetchServiceMap();
        
        try {
            const result = await this.ongoingRequest;
            this.cacheTimestamp = now;
            this.serviceMap = result;
            return result;
        } finally {
            this.ongoingRequest = null;
        }
    }
    
    /**
     * Internal method to fetch service map from API
     */
    private static async fetchServiceMap(): Promise<any> {
        const api = await this.getSystemSettingsApi();
        const response = await api.getAll({ filter: "type|general_relation|=" });
        if (response?.length > 0) {
            return response[0].data;
        }
        return null;
    }

    /**
     * Reset/invalidate cache - forces fresh data on next request
     */
    static resetServiceMap() {
        this.serviceMap = null;
        this.cacheTimestamp = 0;
        this.ongoingRequest = null;
    }
    
    /**
     * Check if cached data is still valid
     */
    static isCacheValid(): boolean {
        return this.serviceMap && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION;
    }
    
    /**
     * Get cached data directly without API call (returns null if cache invalid)
     */
    static getCachedData(): any {
        return this.isCacheValid() ? this.serviceMap : null;
    }
}
